import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  addBoardMemberResultSchema,
  boardBackgroundCustomApplyBodySchema,
  boardBackgroundPutSchema,
  boardBackgroundSchema,
  boardBackgroundUploadConfirmBodySchema,
  boardBackgroundUploadConfirmResponseSchema,
  boardBackgroundUploadContentTypeSchema,
  boardBackgroundUploadPresignBodySchema,
  boardBackgroundUploadPresignSchema,
  boardCommentingSchema,
  customBoardBackgroundListSchema,
  boardListSchema,
  boardMemberListSchema,
  boardMemberPermissionPatchSchema,
  boardMemberPermissionSchema,
  boardMemberSchema,
  boardSchema,
  boardSelfJoinPatchSchema,
  boardSelfJoinSchema,
  commentingPermissionPatchSchema,
  type AddBoardMemberResult,
  type ApiError,
  type Board,
  type BoardBackgroundCustomApplyBody,
  type BoardBackground,
  type BoardBackgroundPut,
  type BoardBackgroundUploadConfirmBody,
  type BoardBackgroundUploadConfirmResponse,
  type BoardBackgroundUploadPresign,
  type BoardBackgroundUploadPresignBody,
  type BoardCommenting,
  type CustomBoardBackground,
  type BoardListState,
  type BoardAddress,
  type BoardContents,
  type BoardMember,
  type BoardMemberPermission,
  type BoardMemberPermissionPatch,
  type BoardMemberWithHistory,
  type BoardRole,
  type BoardSelfJoin,
  type BoardSelfJoinPatch,
  type BoardVisibility,
  type BoardVisibilityPatch,
  type CommentingPermissionPatch,
  type MemberAddRemovePermission,
} from '@molt/shared';
import { del, emptyResponseSchema, get, patch, post, put } from './api';
import { announce } from './announce';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from './interface-language';
import { optimisticMutation } from './optimistic';
import { queryKeys, type MoltQueryKey } from './query-keys';

/**
 * The board **lifecycle** endpoints and their hooks — chunk `D3`, agent A
 * (`FE-08.1`, `FE-08.2`, `FE-08.3`, `FE-08.4`).
 *
 * `lib/workspaces.ts` is the house pattern this file follows: endpoint
 * functions spelling their own path on top of `./api`'s verbs, then hooks whose
 * `onSuccess` writes the cache and never announces. `D3-BRIEF.md` §6.1 is the
 * contract. Four things are restated here rather than at each hook, because
 * each is a defect that does not show up red:
 *
 *  - ⚠ **`closeBoard` and `reopenBoard` are bodyless `POST`s.** Call
 *    `post(path, schema)` with **no third argument**. Fastify refuses
 *    `content-type: application/json` with no body
 *    (`FST_ERR_CTP_EMPTY_JSON_BODY`), and `./api` sends that header only when
 *    `init.body !== undefined`. Passing `{}` sends a body and 400s — and the
 *    round trip is fast enough that it reads as the click never registering.
 *  - ⚠ **`deleteBoard` answers 204**, so it takes the empty-response schema,
 *    never `boardSchema`.
 *  - ⚠ **`queryKeys.boards.page(state)` is `['boards', 'page', state]` and sits
 *    under no per-board prefix**, so a live socket frame never sweeps it. Every
 *    mutation here invalidates that prefix itself.
 *  - **Rename is optimistic; close, reopen and delete are not.** Rename is a
 *    text field somebody is looking at. The other three sit behind an already
 *    `busy` button and a confirmation, and an optimistic delete would have to
 *    un-navigate on rollback.
 *
 * ⚠ **Nothing on the wire says whether the caller may close this board.**
 * `boardCapabilities` derives what it can from the board member list and is
 * knowingly incomplete — see `D3-BRIEF.md` §3.3 for the case it gets wrong (a
 * workspace admin holding no `board_members` row) and why that is the right
 * trade rather than an oversight.
 *
 * ⚠ **Every mutation in this file announces its own rollback**, on failure,
 * through `announceRollback` below — `D3-BRIEF.md` §5.7's "a mutation fails →
 * `announceRollback()`, always" applies to all five hooks, not only the
 * optimistic one. A component composing these hooks (`BoardMenuPanel`,
 * `BoardDangerConfirm`) must **not** also render the mutation's error through
 * its own `Notice`/`role="alert"` — that would be the same event through two
 * channels, which §5.7's one-channel rule forbids. Success announcements are
 * a different matter and stay the caller's, per this file's own house rule
 * above ("onSuccess writes the cache and never announces").
 */

export interface CreateBoardVariables {
  workspaceId: string;
  title: string;
  background?: BoardBackgroundPut | undefined;
  /**
   * `BE-09.4` / `FR-BOARD-001` — *"with private and public alternatives
   * selectable before creation"*.
   *
   * ⚠ **OMITTED MEANS "THE SERVER DECIDES", AND THAT IS THE WHOLE CONTRACT.**
   * `undefined` is not a synonym for `'workspace_visible'`. The route resolves
   * an absent key through `enterpriseDefaultBoardVisibility`, which for an
   * Enterprise-linked workspace can answer a *different* level —
   * `FR-PERM-033` AC4 is precisely that behaviour. So a caller must pass a
   * level only when a user actually chose one; passing the level the UI merely
   * rendered would freeze today's answer into every request and silently
   * override the Enterprise's default.
   */
  visibility?: BoardVisibility | undefined;
}

/**
 * `POST /boards`. `createBoardBodySchema` is **strict** (`D3-BRIEF.md` §3.4) —
 * an unrecognised key is a 422, not a stripped field — so the body is built
 * explicitly from the keys this route accepts rather than forwarded wholesale,
 * which would silently widen the moment a caller's variables object grows a
 * field the route does not declare.
 *
 * ⚠ **`visibility` IS SPREAD-OMITTED, NOT PASSED AS `undefined`** — the shape
 * `background` already uses, and here it carries meaning rather than tidiness.
 * `JSON.stringify` would drop a literal `visibility: undefined` anyway, so the
 * wire is identical either way today; the spread is what keeps the *code* from
 * being one refactor away from sending `null`, which is a value the strict
 * body would 422 on rather than read as "no choice".
 *
 * ⚠ **This function is deliberately not a place where the default is
 * spelled.** `FR-BOARD-001` AC3 — *"given the user has not changed the
 * visibility control"* — is expressed by the ABSENCE of the key, all the way
 * from `BoardCreateForm`'s untouched control to this body. There is no
 * `?? 'workspace_visible'` here, and adding one would be the defect described
 * on `CreateBoardVariables['visibility']` above.
 */
export function createBoard(variables: CreateBoardVariables): Promise<Board> {
  const body = {
    workspaceId: variables.workspaceId,
    title: variables.title,
    ...(variables.background === undefined ? {} : { background: variables.background }),
    ...(variables.visibility === undefined ? {} : { visibility: variables.visibility }),
  };
  return post('/boards', boardSchema, body);
}

export function renameBoard(boardId: string, title: string): Promise<Board> {
  return patch(`/boards/${encodeURIComponent(boardId)}/title`, boardSchema, { title });
}

/** ⚠ Bodyless `POST` — no third argument. See the file doc comment. */
export function closeBoard(boardId: string): Promise<Board> {
  return post(`/boards/${encodeURIComponent(boardId)}/close`, boardSchema);
}

/** ⚠ Bodyless `POST` — no third argument. See the file doc comment. */
export function reopenBoard(boardId: string): Promise<Board> {
  return post(`/boards/${encodeURIComponent(boardId)}/reopen`, boardSchema);
}

/** ⚠ 204 — the empty-response schema, never `boardSchema`. */
export function deleteBoard(boardId: string): Promise<null> {
  return del(`/boards/${encodeURIComponent(boardId)}`, emptyResponseSchema);
}

export function getWorkspaceBoards(
  workspaceId: string,
  state: BoardListState = 'open',
): Promise<Board[]> {
  return get(
    `/workspaces/${encodeURIComponent(workspaceId)}/boards?state=${encodeURIComponent(state)}`,
    boardListSchema,
  );
}

export function getBoardMembers(boardId: string): Promise<BoardMemberWithHistory[]> {
  return get(`/boards/${encodeURIComponent(boardId)}/members`, boardMemberListSchema);
}

export function getBoardBackground(boardId: string): Promise<BoardBackground> {
  return get(`/boards/${encodeURIComponent(boardId)}/background`, boardBackgroundSchema);
}

export function updateBoardBackground(
  boardId: string,
  background: BoardBackgroundPut,
): Promise<BoardBackground> {
  return put(
    `/boards/${encodeURIComponent(boardId)}/background`,
    boardBackgroundSchema,
    boardBackgroundPutSchema.parse(background),
  );
}

export function getCustomBoardBackgrounds(
  boardId: string,
): Promise<CustomBoardBackground[]> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/background/custom`,
    customBoardBackgroundListSchema,
  );
}

export function presignCustomBoardBackground(
  boardId: string,
  body: BoardBackgroundUploadPresignBody,
): Promise<BoardBackgroundUploadPresign> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/background/custom/presign`,
    boardBackgroundUploadPresignSchema,
    boardBackgroundUploadPresignBodySchema.parse(body),
  );
}

export async function uploadToPresignedUrl(
  presign: Pick<BoardBackgroundUploadPresign, 'url' | 'headers'>,
  file: File,
): Promise<void> {
  const response = await fetch(presign.url, {
    method: 'PUT',
    headers: presign.headers,
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Upload failed with ${response.status}`);
  }
}

export function confirmCustomBoardBackground(
  boardId: string,
  body: BoardBackgroundUploadConfirmBody,
): Promise<BoardBackgroundUploadConfirmResponse> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/background/custom`,
    boardBackgroundUploadConfirmResponseSchema,
    boardBackgroundUploadConfirmBodySchema.parse(body),
  );
}

export function applyCustomBoardBackground(
  boardId: string,
  backgroundId: string,
  body: BoardBackgroundCustomApplyBody,
): Promise<BoardBackground> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/background/custom/${encodeURIComponent(
      backgroundId,
    )}/apply`,
    boardBackgroundSchema,
    boardBackgroundCustomApplyBodySchema.parse(body),
  );
}

// ---------------------------------------------------------------------------
// Query options / read hook
// ---------------------------------------------------------------------------

export function workspaceBoardsQueryOptions(
  workspaceId: string,
  state: BoardListState = 'open',
): UseQueryOptions<Board[], ApiError> {
  return {
    queryKey: queryKeys.workspaces.boards(workspaceId, state),
    queryFn: () => getWorkspaceBoards(workspaceId, state),
  };
}

export function boardMembersQueryOptions(
  boardId: string,
): UseQueryOptions<BoardMemberWithHistory[], ApiError> {
  return {
    queryKey: queryKeys.boards.members(boardId),
    queryFn: () => getBoardMembers(boardId),
  };
}

export function boardBackgroundQueryOptions(
  boardId: string,
): UseQueryOptions<BoardBackground, ApiError> {
  return {
    queryKey: queryKeys.boards.background(boardId),
    queryFn: () => getBoardBackground(boardId),
  };
}

export function customBoardBackgroundsQueryOptions(
  boardId: string,
): UseQueryOptions<CustomBoardBackground[], ApiError> {
  return {
    queryKey: queryKeys.boards.customBackgrounds(boardId),
    queryFn: () => getCustomBoardBackgrounds(boardId),
  };
}

/**
 * `GET /boards/:id/members`, gated by `enabled` — §6.1: the panel only fetches
 * while it is open (`useBoardMembers(board.id, open)`), so a board whose menu
 * is never opened never pays for the member list at all.
 */
export function useBoardMembers(
  boardId: string,
  enabled: boolean,
): UseQueryResult<BoardMemberWithHistory[], ApiError> {
  return useQuery({ ...boardMembersQueryOptions(boardId), enabled });
}

export function useUpdateBoardBackground(
  boardId: string,
): UseMutationResult<BoardBackground, ApiError, BoardBackgroundPut> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (background) => updateBoardBackground(boardId, background),
    onSuccess: (background) => {
      writeBoardBackgroundCaches(queryClient, boardId, background);
    },
  });
}

export interface UploadCustomBoardBackgroundVariables {
  readonly file: File;
  readonly renderMode: BoardBackgroundUploadConfirmBody['renderMode'];
  readonly textTreatment: BoardBackgroundUploadConfirmBody['textTreatment'];
  readonly applyToBoard: boolean;
}

export interface ApplyCustomBoardBackgroundVariables {
  readonly backgroundId: string;
  readonly renderMode: BoardBackgroundCustomApplyBody['renderMode'];
  readonly textTreatment: BoardBackgroundCustomApplyBody['textTreatment'];
}

export function useCustomBoardBackgrounds(
  boardId: string,
  enabled: boolean,
): UseQueryResult<CustomBoardBackground[], ApiError> {
  return useQuery({ ...customBoardBackgroundsQueryOptions(boardId), enabled });
}

export function useUploadCustomBoardBackground(
  boardId: string,
): UseMutationResult<
  BoardBackgroundUploadConfirmResponse,
  ApiError,
  UploadCustomBoardBackgroundVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (variables) => {
      const contentType = boardBackgroundUploadContentTypeSchema.parse(
        variables.file.type,
      );
      const presign = await presignCustomBoardBackground(boardId, {
        fileName: variables.file.name,
        contentType,
        sizeBytes: variables.file.size,
      });
      await uploadToPresignedUrl(presign, variables.file);
      return confirmCustomBoardBackground(boardId, {
        storageKey: presign.storageKey,
        renderMode: variables.renderMode,
        textTreatment: variables.textTreatment,
        applyToBoard: variables.applyToBoard,
      });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(
        queryKeys.boards.customBackgrounds(boardId),
        (previous) => {
          const rows = (previous ?? []) as CustomBoardBackground[];
          if (rows.some((row) => row.id === result.background.id)) return rows;
          return [result.background, ...rows];
        },
      );
      writeBoardBackgroundCaches(queryClient, boardId, result.boardBackground);
    },
  });
}

export function useApplyCustomBoardBackground(
  boardId: string,
): UseMutationResult<BoardBackground, ApiError, ApplyCustomBoardBackgroundVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) =>
      applyCustomBoardBackground(boardId, variables.backgroundId, {
        renderMode: variables.renderMode,
        textTreatment: variables.textTreatment,
      }),
    onSuccess: (background) => {
      writeBoardBackgroundCaches(queryClient, boardId, background);
    },
  });
}

function writeBoardBackgroundCaches(
  queryClient: QueryClient,
  boardId: string,
  background: BoardBackground,
): void {
  queryClient.setQueryData(queryKeys.boards.background(boardId), background);
  queryClient.setQueriesData<BoardContents>(
    { queryKey: queryKeys.boards.contents(boardId) },
    (previous) =>
      previous ? { ...previous, board: { ...previous.board, background } } : previous,
  );
  invalidateBoardsPage(queryClient);
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * The caller's own row, matched on `users.id` — `boardMemberSchema.id` and
 * `ownProfileSchema.id` both come from `publicProfileSchema`, so they compare
 * directly. `null` when the caller holds no row on this board, on
 * `lib/workspaces.ts`'s `ownRole` footing: a control that appears a moment
 * late is better than one that appears and 403s.
 */
export function ownBoardRole(
  members: readonly BoardMember[] | undefined,
  ownId: string | undefined,
): BoardRole | null {
  return ownBoardMember(members, ownId)?.role ?? null;
}

export function ownBoardMember(
  members: readonly BoardMember[] | undefined,
  ownId: string | undefined,
): BoardMember | null {
  if (members === undefined || ownId === undefined) return null;
  return members.find((member) => member.id === ownId) ?? null;
}

export interface BoardCapabilities {
  /**
   * `D3-BRIEF.md` §5.5: mirrors `lib/board-face.ts`'s `canEdit` (`!closed`)
   * rather than folding in role — that widening is `FE-09.2`'s (chunk `D4`),
   * and `BoardTitle` in this chunk is wired from `canEdit` directly, not from
   * this field. Kept here only because it is part of the fixed §6.1 shape;
   * it has no consumer within `D3`.
   */
  canRename: boolean;
  /** `FR-EXP-012` AC4: delete is not *offered* on an open board, at any role. */
  deleteOffered: boolean;
  canClose: boolean;
  canDelete: boolean;
  /** Non-null exactly when the matching `can*` is false and the control still renders. */
  closeDisabledReason: string | null;
  deleteDisabledReason: string | null;

  /**
   * `FR-PERM-012` — `PATCH /boards/:id/visibility` sits on
   * `requireBoardAdminRole`. `D4-BRIEF.md` §5.3 derived this from the literal
   * board role, because that guard then refused a workspace admin holding no
   * `board_members` row. ⚠ **That is stale on the server side**: since
   * `BE-39.3` the guard tests `access.isAdmin`, which admits that workspace
   * admin and, since SA-5, a platform superadmin. This derivation follows the
   * superadmin (`isSuper` below) but still locks the workspace admin — the
   * pre-existing `BE-39.3` lockout `docs/notes/superadmin-handoff.md` records as
   * a known gap, not fixed by the flag. When `BE-39.3` ships a server-side
   * capability, prefer it and delete this derivation.
   */
  canChangeVisibility: boolean;
  /** Non-null exactly when `canChangeVisibility` is false. */
  visibilityDisabledReason: string | null;
  /**
   * `requireBoardMemberManager`'s ladder, client-side: admin always; a normal
   * member only while `memberAddRemovePermission === 'all_members'` (the
   * default); an observer never; a non-member never. `D4-BRIEF.md` §5.6.
   */
  canManageMembers: boolean;
  /** Non-null exactly when `canManageMembers` is false. */
  manageMembersDisabledReason: string | null;
}

type BoardHooksTranslate = InterfaceLanguageContextValue['t'];

/**
 * The §3.3/§2.2/§2.3 derivation, all in one pure function.
 *
 * - `rolesKnown: false` never enables a destructive control by default —
 *   `canClose`/`canDelete` are both `false` with the "still checking" reason,
 *   on the reasoning stated at length in `D3-BRIEF.md` §3.3: an incomplete
 *   derivation must fail closed, not open.
 * - `deleteOffered` is a **state** fact (`FR-EXP-012` AC4), independent of
 *   role: an open board offers no delete entry to anybody, admin included, so
 *   `deleteDisabledReason` stays `null` while the control is simply absent —
 *   a reason attached to a control nobody renders would be a phantom one.
 * - `canClose` governs the toggle in general (close *or* reopen — both are
 *   `requireBoardAdmin` server-side); which direction a caller offers is the
 *   composing component's business, not this function's.
 */
export function boardCapabilities(input: {
  role: BoardRole | null;
  closed: boolean;
  rolesKnown: boolean;
  /**
   * `undefined` until `GET /boards/:id/member-permission` settles — and
   * `undefined` is **not** `'admins'`. While it is unknown `canManageMembers`
   * is false and the add form does not render; it appears when the answer
   * arrives. `D4-BRIEF.md` §5.6.
   */
  memberPermission?: MemberAddRemovePermission | undefined;
  /**
   * `isSuperadminSession(session)` — SA-9. The server folds the flag into
   * `BoardAccess.isAdmin` and into the reachable member-manager predicate, so it
   * counts as adminship for every capability here. Never a synthesised `role`:
   * `ownBoardRole` keeps answering the real row. Required, like `canEdit`'s.
   */
  isSuper: boolean;
  t: BoardHooksTranslate;
}): BoardCapabilities {
  const { role, closed, rolesKnown, memberPermission, isSuper, t } = input;
  const isAdmin = role === 'admin' || isSuper;
  const deleteOffered = closed;

  const canClose = rolesKnown && isAdmin;
  const closeDisabledReason = canClose
    ? null
    : rolesKnown
      ? t('primitives.boardHooks.closeDisabled')
      : t('primitives.boardHooks.checkingAccess');

  const canDelete = deleteOffered && rolesKnown && isAdmin;
  const deleteDisabledReason = !deleteOffered
    ? null
    : canDelete
      ? null
      : rolesKnown
        ? t('primitives.boardHooks.deleteDisabled')
        : t('primitives.boardHooks.checkingAccess');

  /**
   * ⚠ `D4-BRIEF.md` §6.2 asks agent A to verify the `!closed` conjunct on
   * both new fields rather than assume it. Verified against
   * `apps/server/src/routes/boards.ts`: `PATCH /boards/:id/visibility`,
   * `POST /boards/:id/members`, `DELETE /boards/:id/members/:userId` and
   * `PATCH /boards/:id/members/:userId/role` **all** call `requireBoard*`
   * first and then `if (access.closed) throw new ApiError('conflict', …)`
   * — the refusal fires for every role, admin included, before the
   * handler does anything else. So a closed board 409s regardless of who
   * is asking, exactly like `canDelete`'s `deleteOffered` gate one section
   * up: offering the control on a closed board would be a control that
   * always fails. `!closed` stays.
   */
  const canChangeVisibility = rolesKnown && isAdmin && !closed;
  const visibilityDisabledReason = canChangeVisibility
    ? null
    : rolesKnown
      ? t('primitives.boardHooks.visibilityDisabled')
      : t('primitives.boardHooks.checkingAccess');

  const canManageMembers =
    rolesKnown &&
    !closed &&
    (isAdmin || (role === 'normal' && memberPermission === 'all_members'));
  const manageMembersDisabledReason = canManageMembers
    ? null
    : rolesKnown
      ? t('primitives.boardHooks.manageMembersDisabled')
      : t('primitives.boardHooks.checkingAccess');

  return {
    canRename: !closed,
    deleteOffered,
    canClose,
    canDelete,
    closeDisabledReason,
    deleteDisabledReason,
    canChangeVisibility,
    visibilityDisabledReason,
    canManageMembers,
    manageMembersDisabledReason,
  };
}

// ---------------------------------------------------------------------------
// The page-prefix invalidation every mutation shares — §5.6
// ---------------------------------------------------------------------------

/**
 * `queryKeys.boards.page('open')` and `.page('closed')` are `['boards',
 * 'page', 'open'|'closed']` — this is their shared prefix. Invalidating it
 * sweeps both states in one call. It is **not** `queryKeys.boards.all()`
 * (`['boards']`): that would also invalidate every `boards.detail`/`contents`
 * entry this file writes directly, forcing an unwanted extra refetch of the
 * board the caller is already looking at.
 */
const BOARDS_PAGE_PREFIX: MoltQueryKey = ['boards', 'page'];

/**
 * `queryKeys.boards.byShortLink(shortLink)` is `['boards', 'by-short-link',
 * shortLink]` — this is the prefix with `shortLink` left off, for finding
 * *whichever* short-link entries are cached without already knowing the
 * short link. See `useRenameBoard` and `writeClosedFlag` for why: this file's
 * hooks are handed a `boardId`, never a `shortLink` (§6.1's fixed
 * signatures), so the only way to reach `boards.byShortLink`'s cache entry is
 * to search the cache for one whose `id` matches.
 */
const BOARDS_BY_SHORT_LINK_PREFIX: MoltQueryKey = ['boards', 'by-short-link'];

function invalidateBoardsPage(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: BOARDS_PAGE_PREFIX });
}

// ---------------------------------------------------------------------------
// Rollback announcements — §5.7: every mutation in this file announces its
// own rollback, on `lists.ts`'s `announceRollback` footing. A call site can
// forget; a hook cannot.
// ---------------------------------------------------------------------------

function announceRollback<TVariables, TContext>(
  handlers: {
    onError: (error: unknown, variables: TVariables, context: TContext) => void;
  },
  message: (variables: TVariables) => string,
): (error: unknown, variables: TVariables, context: TContext) => void {
  return (error, variables, context) => {
    handlers.onError(error, variables, context);
    announce(message(variables));
  };
}

// ---------------------------------------------------------------------------
// useCreateBoard
// ---------------------------------------------------------------------------

export function useCreateBoard(): UseMutationResult<
  Board,
  ApiError,
  CreateBoardVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: createBoard,
    onSuccess: () => {
      invalidateBoardsPage(queryClient);
    },
    onError: (_error, variables) => {
      announce(t('primitives.boardHooks.createFailed', { title: variables.title }));
    },
  });
}

// ---------------------------------------------------------------------------
// useRenameBoard — the one optimistic mutation in this file (§5.6)
// ---------------------------------------------------------------------------

/**
 * The two cache shapes a rename can touch. Discriminated structurally — a
 * `BoardContents` entry carries a `board` key, a `BoardAddress` entry does
 * not (`BoardAddress` = `boardSchema.extend({ path })`) — rather than by
 * which key produced it, so the same `updater` serves both without the
 * `optimisticMutation` module needing to know this file's key spellings.
 */
type RenameCacheEntry = BoardContents | BoardAddress;

function isBoardContentsEntry(entry: RenameCacheEntry): entry is BoardContents {
  return 'board' in entry;
}

/**
 * `useRenameBoard(boardId)` is handed no `shortLink` (§6.1's fixed
 * signature), so `boards.byShortLink(shortLink)`'s cache entry cannot be
 * addressed by key. Instead: search whatever `by-short-link` entries are
 * currently cached and keep the ones whose `id` is this board's — at most
 * one in practice (a board has one short link), but this makes no such
 * assumption. A board never opened via its short-link route simply
 * contributes no key here, and `boards.contents(boardId)` is written either
 * way.
 */
function renameAffectedKeys(queryClient: QueryClient, boardId: string): MoltQueryKey[] {
  const shortLinkKeys = queryClient
    .getQueryCache()
    .findAll({ queryKey: BOARDS_BY_SHORT_LINK_PREFIX })
    .filter((query) => (query.state.data as BoardAddress | undefined)?.id === boardId)
    .map((query) => query.queryKey);

  return [queryKeys.boards.contents(boardId), ...shortLinkKeys];
}

export function useRenameBoard(
  boardId: string,
): UseMutationResult<Board, ApiError, { title: string }> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  const handlers = optimisticMutation<{ title: string }, RenameCacheEntry>({
    queryClient,
    keys: () => renameAffectedKeys(queryClient, boardId),
    updater: (previous, variables) => {
      if (previous === undefined) return undefined;
      if (isBoardContentsEntry(previous)) {
        return { ...previous, board: { ...previous.board, title: variables.title } };
      }
      return { ...previous, title: variables.title };
    },
  });

  return useMutation({
    mutationFn: ({ title }: { title: string }) => renameBoard(boardId, title),
    ...handlers,
    onError: announceRollback(handlers, () => t('primitives.boardHooks.renameFailed')),
    // §5.6: invalidate the page prefix "on settle", alongside — not instead
    // of — the per-key invalidation `handlers.onSettled` already performs.
    // Spreading `...handlers` and then declaring a bare `onSettled:` here
    // would REPLACE `handlers.onSettled` rather than add to it (the same
    // trap `announceRollback` exists to avoid for `onError`), so this wraps
    // it the same way.
    onSettled: (data, error, variables, context) => {
      handlers.onSettled(data, error, variables, context);
      invalidateBoardsPage(queryClient);
    },
  });
}

// ---------------------------------------------------------------------------
// useCloseBoard / useReopenBoard — not optimistic (§5.6)
// ---------------------------------------------------------------------------

/**
 * Writes the `closed` flag into the two entries a rename would have touched
 * — `boards.contents(boardId)` directly, and whichever `boards.byShortLink`
 * entries are cached for this board, found the same way `useRenameBoard`
 * finds them (§6.1 hands this file a `boardId`, never a `shortLink`).
 *
 * Called from `onSuccess`, never optimistically — close/reopen sit behind an
 * already-`busy` button and (for close) a confirmation, so there is nothing
 * an optimistic write would make feel faster (§5.6).
 */
function writeClosedFlag(
  queryClient: QueryClient,
  boardId: string,
  closed: boolean,
): void {
  queryClient.setQueryData<BoardContents>(
    queryKeys.boards.contents(boardId),
    (previous) =>
      previous ? { ...previous, board: { ...previous.board, closed } } : previous,
  );
  queryClient.setQueriesData<BoardAddress>(
    { queryKey: BOARDS_BY_SHORT_LINK_PREFIX },
    (previous) =>
      previous && previous.id === boardId ? { ...previous, closed } : previous,
  );
}

export function useCloseBoard(boardId: string): UseMutationResult<Board, ApiError, void> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: () => closeBoard(boardId),
    onSuccess: (updated) => {
      writeClosedFlag(queryClient, boardId, updated.closed);
      invalidateBoardsPage(queryClient);
    },
    onError: () => announce(t('primitives.boardHooks.closeFailed')),
  });
}

export function useReopenBoard(
  boardId: string,
): UseMutationResult<Board, ApiError, void> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: () => reopenBoard(boardId),
    onSuccess: (updated) => {
      writeClosedFlag(queryClient, boardId, updated.closed);
      invalidateBoardsPage(queryClient);
    },
    onError: () => announce(t('primitives.boardHooks.reopenFailed')),
  });
}

// ---------------------------------------------------------------------------
// useDeleteBoard — not optimistic; the host un-navigates on rollback, not
// this file (§5.6)
// ---------------------------------------------------------------------------

export function useDeleteBoard(boardId: string): UseMutationResult<null, ApiError, void> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: () => deleteBoard(boardId),
    onSuccess: () => {
      // `boards.detail(boardId)` is `['boards', boardId]` — a prefix of
      // `contents`, `lists` and `members` too, so this one call removes
      // every entry this board's id was ever cached under. The
      // `by-short-link` entry is left alone deliberately — §5.6 names only
      // this prefix, and a stale short-link entry for a deleted board simply
      // 404s the next time anything fetches it.
      queryClient.removeQueries({ queryKey: queryKeys.boards.detail(boardId) });
      invalidateBoardsPage(queryClient);
    },
    onError: () => announce(t('primitives.boardHooks.deleteFailed')),
  });
}

// ===========================================================================
// D4 — visibility and membership (FE-09.1, FE-10.1, FE-10.2)
//
// The six endpoints and four mutation hooks `D4-BRIEF.md` §6.2 fixes.
//
// ⚠ Nothing here is optimistic, and §5.7 argues why: every one of these sits
//    behind a discrete control that can render `busy`, and three of the four
//    are refusable for reasons the client cannot predict (last admin, closed
//    board, no such account).
//
// ⚠ **Every mutation below announces its own rollback**, on the file's own
//    house rule stated at the top — but unlike `useCloseBoard` et al.'s fixed
//    strings, each `onError` here announces `error.message` **verbatim**.
//    §5.9 requires the last-admin 409 (and every other refusal) to be
//    renderable **at the row** by the composing component, which reads it off
//    `mutation.error.message` — `onError` is a side effect and does not
//    replace that state, so nothing here "swallows" the `ApiError`. Using the
//    server's own words for the `announce()` call, rather than a generic
//    fallback, is what keeps the screen-reader channel and the visual row
//    channel saying the same thing (§5.9's "never … a rollback announcement
//    that says something generic").
// ===========================================================================

export function getBoardMemberPermission(
  boardId: string,
): Promise<BoardMemberPermission> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/member-permission`,
    boardMemberPermissionSchema,
  );
}

export function updateBoardMemberPermission(
  boardId: string,
  body: BoardMemberPermissionPatch,
): Promise<BoardMemberPermission> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/member-permission`,
    boardMemberPermissionSchema,
    boardMemberPermissionPatchSchema.parse(body),
  );
}

export function boardMemberPermissionQueryOptions(
  boardId: string,
): UseQueryOptions<BoardMemberPermission, ApiError> {
  return {
    queryKey: queryKeys.boards.memberPermission(boardId),
    queryFn: () => getBoardMemberPermission(boardId),
  };
}

/**
 * ⚠ `enabled` is the **session** gate, never an error branch — `D4-BRIEF.md`
 * §3.4. This route is `requireBoardReader`, so it does not 401 the way
 * `/members` does; the gate is here for symmetry with `useBoardMembers` and
 * because a signed-out viewer has no use for the answer.
 */
export function useBoardMemberPermission(
  boardId: string,
  enabled: boolean,
): UseQueryResult<BoardMemberPermission, ApiError> {
  return useQuery({ ...boardMemberPermissionQueryOptions(boardId), enabled });
}

export function getBoardCommenting(boardId: string): Promise<BoardCommenting> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/commenting-permission`,
    boardCommentingSchema,
  );
}

export function updateBoardCommenting(
  boardId: string,
  body: CommentingPermissionPatch,
): Promise<BoardCommenting> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/commenting-permission`,
    boardCommentingSchema,
    commentingPermissionPatchSchema.parse(body),
  );
}

export function boardCommentingQueryOptions(
  boardId: string,
): UseQueryOptions<BoardCommenting, ApiError> {
  return {
    queryKey: queryKeys.boards.commentingPermission(boardId),
    queryFn: () => getBoardCommenting(boardId),
  };
}

export function useBoardCommenting(
  boardId: string,
  enabled: boolean,
): UseQueryResult<BoardCommenting, ApiError> {
  return useQuery({ ...boardCommentingQueryOptions(boardId), enabled });
}

export function getBoardSelfJoin(boardId: string): Promise<BoardSelfJoin> {
  return get(`/boards/${encodeURIComponent(boardId)}/self-join`, boardSelfJoinSchema);
}

export function updateBoardSelfJoin(
  boardId: string,
  body: BoardSelfJoinPatch,
): Promise<BoardSelfJoin> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/self-join`,
    boardSelfJoinSchema,
    boardSelfJoinPatchSchema.parse(body),
  );
}

export function boardSelfJoinQueryOptions(
  boardId: string,
): UseQueryOptions<BoardSelfJoin, ApiError> {
  return {
    queryKey: queryKeys.boards.selfJoin(boardId),
    queryFn: () => getBoardSelfJoin(boardId),
  };
}

export function useBoardSelfJoin(
  boardId: string,
  enabled: boolean,
): UseQueryResult<BoardSelfJoin, ApiError> {
  return useQuery({ ...boardSelfJoinQueryOptions(boardId), enabled });
}

export function updateBoardVisibility(
  boardId: string,
  body: BoardVisibilityPatch,
): Promise<Board> {
  return patch(`/boards/${encodeURIComponent(boardId)}/visibility`, boardSchema, body);
}

export function addBoardMember(
  boardId: string,
  handle: { email: string } | { username: string },
): Promise<AddBoardMemberResult> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/members`,
    addBoardMemberResultSchema,
    handle,
  );
}

export function removeBoardMember(boardId: string, userId: string): Promise<null> {
  return del(
    `/boards/${encodeURIComponent(boardId)}/members/${encodeURIComponent(userId)}`,
    emptyResponseSchema,
  );
}

export function updateBoardMemberRole(
  boardId: string,
  userId: string,
  role: BoardRole,
): Promise<BoardMember> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/members/${encodeURIComponent(userId)}/role`,
    boardMemberSchema,
    { role },
  );
}

/**
 * Writes the new `visibility` into the same two entries `writeClosedFlag`
 * writes `closed` into, above — `boards.contents(boardId)` directly, and
 * whichever `boards.byShortLink` entries are cached for this board.
 *
 * ⚠ **Considered and rejected: generalising `renameAffectedKeys` for this**
 * (`D4-BRIEF.md` §6.2 asks the question explicitly). `renameAffectedKeys`
 * returns a *list of keys* for `optimisticMutation` to apply one `updater`
 * function across, which is the right shape for an **optimistic** mutation
 * that must round-trip through `isBoardContentsEntry`'s structural
 * discriminant before the response exists. Visibility is not optimistic
 * (§5.7) — the value being written is the server's own `updated.visibility`,
 * known only in `onSuccess` — so there is nothing to discriminate ahead of
 * time and no rollback context to thread through. `writeClosedFlag` is the
 * precedent that already matches this shape exactly (a known post-image,
 * written into both entries in `onSuccess`), so this function copies *that*
 * one instead, field swapped. `renameAffectedKeys` itself is untouched and
 * keeps its one caller (`useRenameBoard`).
 */
function writeBoardVisibility(
  queryClient: QueryClient,
  boardId: string,
  visibility: BoardVisibility,
): void {
  queryClient.setQueryData<BoardContents>(
    queryKeys.boards.contents(boardId),
    (previous) =>
      previous ? { ...previous, board: { ...previous.board, visibility } } : previous,
  );
  queryClient.setQueriesData<BoardAddress>(
    { queryKey: BOARDS_BY_SHORT_LINK_PREFIX },
    (previous) =>
      previous && previous.id === boardId ? { ...previous, visibility } : previous,
  );
}

/**
 * `PATCH /boards/:id/visibility` — §5.7's first row. Writes `boards.contents`
 * and every matching `boards.byShortLink` entry directly (`writeBoardVisibility`,
 * above) rather than invalidating them, because the response carries the
 * complete new value; then invalidates the `['boards','page']` prefix
 * **itself**, on this file's standing reason — `boardPageBoardSchema` carries
 * `visibility`, so a board tile on the landing page goes stale otherwise, and
 * `eventBlastRadius` never reaches that key (`query-keys.ts`).
 */
export function useUpdateBoardVisibility(
  boardId: string,
): UseMutationResult<Board, ApiError, BoardVisibilityPatch> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: BoardVisibilityPatch) => updateBoardVisibility(boardId, body),
    onSuccess: (updated) => {
      writeBoardVisibility(queryClient, boardId, updated.visibility);
      invalidateBoardsPage(queryClient);
    },
    /*
     * ---------------------------------------------------------------------------
     * ⚠ THE ONE HOOK IN THIS FILE THAT DOES **NOT** ANNOUNCE ITS OWN FAILURE,
     *    AND IT IS NOT AN OMISSION
     * ---------------------------------------------------------------------------
     * Every other mutation here announces, because nothing renders its refusal
     * in a live node. This one's refusal **is** rendered live:
     * `BoardVisibilityControl` puts it in a `Notice tone="bad"`, and `Notice`
     * hardcodes `role="alert"` for that tone. Announcing as well would say it
     * twice — the double channel this file's own docstring forbids and
     * `D3-BRIEF.md` §5.7 legislates against.
     *
     * The rule the D4 integration pass settled, stated once so the asymmetry
     * reads as a decision rather than a gap: **the channel is `announce()`
     * unless the rendered error node is itself live.** Exactly one D4 surface
     * renders a live node, and this is its mutation. The member row's slot was
     * made inert for the same rule — see `BoardMemberList.tsx`.
     *
     * ⚠ The error is still on `mutation.error.message` either way; nothing here
     * swallows it, and the visibility row reads it off that.
     */
  });
}

/**
 * `POST /boards/:id/members` — §5.7's second row: invalidates rather than
 * writes, because `boards.members(id)` is a **list** whose ordering
 * (`created_at, user_id`) is the server's to state, and
 * `addBoardMemberResultSchema.added` can be `false` for a member who was
 * already there — nothing this file could construct locally would be
 * trustworthy as the new row's position.
 */
export function useAddBoardMember(
  boardId: string,
): UseMutationResult<
  AddBoardMemberResult,
  ApiError,
  { email: string } | { username: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (handle: { email: string } | { username: string }) =>
      addBoardMember(boardId, handle),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.boards.members(boardId) });
    },
    onError: (error) => announce(error.message),
  });
}

/** `DELETE /boards/:id/members/:userId` — §5.7's third row. Same reasoning as add. */
export function useRemoveBoardMember(
  boardId: string,
): UseMutationResult<null, ApiError, { userId: string }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => removeBoardMember(boardId, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.boards.members(boardId) });
    },
    onError: (error) => announce(error.message),
  });
}

/** `PATCH /boards/:id/members/:userId/role` — §5.7's fourth row. Same reasoning as add. */
export function useUpdateBoardMemberRole(
  boardId: string,
): UseMutationResult<BoardMember, ApiError, { userId: string; role: BoardRole }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: BoardRole }) =>
      updateBoardMemberRole(boardId, userId, role),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.boards.members(boardId) });
    },
    onError: (error) => announce(error.message),
  });
}

export function useUpdateBoardMemberPermission(
  boardId: string,
): UseMutationResult<BoardMemberPermission, ApiError, BoardMemberPermissionPatch> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: BoardMemberPermissionPatch) =>
      updateBoardMemberPermission(boardId, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.boards.memberPermission(boardId), updated);
      void queryClient.invalidateQueries({ queryKey: queryKeys.boards.members(boardId) });
    },
  });
}

export function useUpdateBoardCommenting(
  boardId: string,
): UseMutationResult<BoardCommenting, ApiError, CommentingPermissionPatch> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CommentingPermissionPatch) => updateBoardCommenting(boardId, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.boards.commentingPermission(boardId), updated);
    },
  });
}

export function useUpdateBoardSelfJoin(
  boardId: string,
): UseMutationResult<BoardSelfJoin, ApiError, BoardSelfJoinPatch> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: BoardSelfJoinPatch) => updateBoardSelfJoin(boardId, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.boards.selfJoin(boardId), updated);
    },
  });
}
